import {
  CIVILITY_LABEL,
  CONTACT_STATUS_LABEL,
  ContactStatus,
  WEALTH_TRANCHE_LABEL,
  WealthTranche,
} from '@crm/shared';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  BusinessCardScanner,
  type ScannedPayload,
} from '../components/contacts/BusinessCardScanner';
import { ContactFormModal, type ScanContext } from '../components/contacts/ContactFormModal';
import type { ContactRow, ContactScanResponse } from '../lib/api';
import { apiClient } from '../lib/api';
import { useIsMobileDevice } from '../lib/viewport';

const STATUS_BADGE: Record<ContactStatus, { label: string; classes: string }> = {
  prospect: { label: CONTACT_STATUS_LABEL.prospect, classes: 'bg-platinum/60 text-ink-secondary' },
  qualified_lead: {
    label: CONTACT_STATUS_LABEL.qualified_lead,
    classes: 'bg-glacier text-graphite border border-graphite/20',
  },
  client: {
    label: CONTACT_STATUS_LABEL.client,
    classes: 'bg-bg-success text-semantic-success',
  },
};

export function ContactsPage() {
  const [contacts, setContacts] = useState<ContactRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [q, setQ] = useState('');
  const [wealth, setWealth] = useState<WealthTranche | ''>('');
  const [status, setStatus] = useState<ContactStatus | ''>('');
  const [pepOnly, setPepOnly] = useState(false);

  const [menuOpen, setMenuOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [scanMode, setScanMode] = useState<'camera' | 'file'>('camera');
  const [formOpen, setFormOpen] = useState(false);
  const [prefill, setPrefill] = useState<ContactScanResponse['fields'] | null>(null);
  const [scanContext, setScanContext] = useState<ScanContext | null>(null);

  const menuRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobileDevice();

  useEffect(() => {
    void reload();
    // We intentionally only refetch on user-triggered filter submits below,
    // not on every keystroke — keeps the initial load cheap.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function reload() {
    setLoading(true);
    setError(null);
    try {
      const rows = await apiClient.listContacts({
        q: q.trim() || undefined,
        wealth_tranche: wealth || undefined,
        status: status || undefined,
        pep: pepOnly ? 'true' : undefined,
        limit: 200,
      });
      setContacts(rows);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  // Trigger search on user-driven changes (debounced via blur or Enter).
  function applyFilters(e?: React.FormEvent) {
    e?.preventDefault();
    void reload();
  }

  // Close menu on outside click.
  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [menuOpen]);

  function onScanned({ response, thumbnailDataUrl }: ScannedPayload) {
    setPrefill(response.fields);
    setScanContext({
      thumbnailDataUrl,
      confidence: response.fields.confidence,
      ocrFieldKeys: ocrKeysFromFields(response.fields),
    });
    setScanOpen(false);
    setFormOpen(true);
  }

  function onContactCreated(_id: string) {
    setFormOpen(false);
    setPrefill(null);
    setScanContext(null);
    void reload();
  }

  const sorted = useMemo(() => contacts, [contacts]);

  return (
    <div className="px-8 py-10 max-w-content mx-auto">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="label-uppercase">— 02 / Leads &amp; Contacts</p>
          <h1 className="mt-2 font-display text-4xl text-graphite font-semibold">
            Prospect directory
          </h1>
          <p className="mt-2 text-sm text-ink-secondary">
            {sorted.length} contact{sorted.length > 1 ? 's' : ''} · entry point of the funnel ·
            independent of the pipeline
          </p>
        </div>

        <div className="relative" ref={menuRef}>
          <button
            type="button"
            onClick={() => setMenuOpen((m) => !m)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            className="btn-brand"
          >
            + New contact
          </button>
          {menuOpen && (
            <div
              role="menu"
              className="absolute right-0 mt-2 w-64 bg-surface rounded-card border border-platinum shadow-panel z-30 overflow-hidden"
            >
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setMenuOpen(false);
                  setPrefill(null);
                  setFormOpen(true);
                }}
                className="block w-full text-left px-4 py-3 text-sm hover:bg-glacier"
              >
                <span className="font-medium">Create manually</span>
                <span className="block text-xs text-ink-tertiary mt-0.5">Full contact entry</span>
              </button>
              {isMobile ? (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    setScanMode('camera');
                    setScanOpen(true);
                  }}
                  className="block w-full text-left px-4 py-3 text-sm hover:bg-glacier border-t border-platinum"
                >
                  <span className="font-medium">Scan with camera</span>
                  <span className="block text-xs text-ink-tertiary mt-0.5">
                    Live capture + Anthropic vision OCR
                  </span>
                </button>
              ) : (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    setScanMode('file');
                    setScanOpen(true);
                  }}
                  className="block w-full text-left px-4 py-3 text-sm hover:bg-glacier border-t border-platinum"
                >
                  <span className="font-medium">Import a business card</span>
                  <span className="block text-xs text-ink-tertiary mt-0.5">
                    Photo / scan from disk + OCR
                  </span>
                </button>
              )}
            </div>
          )}
        </div>
      </header>

      <form onSubmit={applyFilters} className="mt-8 card p-5 grid grid-cols-1 md:grid-cols-5 gap-4">
        <label className="block md:col-span-2">
          <span className="block text-xs uppercase tracking-wider text-ink-secondary mb-2">
            Search
          </span>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Name, company, email…"
            className="input-base"
          />
        </label>
        <label className="block">
          <span className="block text-xs uppercase tracking-wider text-ink-secondary mb-2">
            Status
          </span>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as ContactStatus | '')}
            className="select-base"
          >
            <option value="">All</option>
            <option value="prospect">Prospect</option>
            <option value="qualified_lead">Qualified lead</option>
            <option value="client">Client</option>
          </select>
        </label>
        <label className="block">
          <span className="block text-xs uppercase tracking-wider text-ink-secondary mb-2">
            Tranche
          </span>
          <select
            value={wealth}
            onChange={(e) => setWealth(e.target.value as WealthTranche | '')}
            className="select-base"
          >
            <option value="">All</option>
            {Object.entries(WEALTH_TRANCHE_LABEL).map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </select>
        </label>
        <div className="flex items-end gap-3">
          <label className="flex items-center gap-2 text-sm text-ink-secondary">
            <input
              type="checkbox"
              checked={pepOnly}
              onChange={(e) => setPepOnly(e.target.checked)}
            />
            PEP only
          </label>
          <button type="submit" className="btn-secondary ml-auto">
            Filter
          </button>
        </div>
      </form>

      <section className="mt-8 card overflow-hidden">
        {loading && <p className="p-6 text-sm text-ink-tertiary">Loading…</p>}
        {error && <p className="p-6 text-sm text-semantic-error">{error}</p>}
        {!loading && !error && (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-platinum/40 text-left text-[11px] uppercase tracking-wider text-ink-secondary">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Company · position</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Tranche</th>
                <th className="px-4 py-3">RM</th>
                <th className="px-4 py-3 text-right">Last interaction</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((c, idx) => (
                <tr
                  key={c.id}
                  className={
                    'border-t border-platinum hover:bg-glacier/50 transition-colors ' +
                    (idx % 2 === 1 ? 'bg-glacier/30' : '')
                  }
                >
                  <td className="px-4 py-3">
                    <Link
                      to={`/contacts/${c.id}`}
                      className="font-medium text-ink hover:text-graphite"
                    >
                      {c.civility ? `${CIVILITY_LABEL[c.civility]} ` : ''}
                      {c.first_name} {c.last_name}
                    </Link>
                    {c.pep_flag && (
                      <span className="ml-2 text-[10px] uppercase tracking-wider text-signal">
                        PEP
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-ink-secondary">
                    {c.company ? (
                      <>
                        <span className="block">{c.company}</span>
                        {c.position && (
                          <span className="block text-xs text-ink-tertiary">{c.position}</span>
                        )}
                      </>
                    ) : (
                      <span className="text-ink-tertiary">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={
                        'inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-full ' +
                        STATUS_BADGE[c.status].classes
                      }
                    >
                      {STATUS_BADGE[c.status].label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-ink-secondary">
                    {WEALTH_TRANCHE_LABEL[c.wealth_tranche]}
                  </td>
                  <td className="px-4 py-3 text-ink-secondary">
                    {c.owner_rm?.display_name ?? <span className="text-ink-tertiary">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right font-tabular text-ink-secondary">
                    {c.last_interaction_at
                      ? new Date(c.last_interaction_at).toLocaleDateString('fr-CH')
                      : '—'}
                  </td>
                </tr>
              ))}
              {sorted.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-12 text-center text-sm italic text-ink-tertiary"
                  >
                    No contacts match the filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </section>

      <ContactFormModal
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setPrefill(null);
          setScanContext(null);
        }}
        onCreated={onContactCreated}
        prefill={prefill}
        scanContext={scanContext}
      />
      <BusinessCardScanner
        open={scanOpen}
        mode={scanMode}
        onClose={() => setScanOpen(false)}
        onScanned={onScanned}
      />
    </div>
  );
}

/**
 * Compute the set of form-field keys that the OCR actually populated. Used
 * by ContactFormModal to colour the left border on prefilled inputs.
 * Address sub-fields are namespaced `address_*` so they don't collide with
 * top-level form keys.
 */
function ocrKeysFromFields(f: ContactScanResponse['fields']): Set<string> {
  const keys = new Set<string>();
  if (f.firstName) keys.add('first_name');
  if (f.lastName) keys.add('last_name');
  if (f.emails[0]) keys.add('email_primary');
  if (f.emails[1]) keys.add('email_secondary');
  if (f.phones[0]) keys.add('phone_primary');
  if (f.phones[1]) keys.add('phone_secondary');
  if (f.linkedinUrl) keys.add('linkedin_url');
  if (f.company) keys.add('company');
  if (f.title) keys.add('position');
  if (f.addressLine) keys.add('address_street');
  if (f.postalCode) keys.add('address_postal_code');
  if (f.city) keys.add('address_city');
  if (f.country) keys.add('address_country');
  return keys;
}
